import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import debounce from 'lodash/debounce';
import PhoneNumberManager from '../sales/shared/PhoneNumberManager';

const AddReferralItemForm = ({ referral, referralTypes, referralStatuses, onSuccess, onCancel }) => {
  // Common referral types with icons
  const commonReferralTypes = [
    { name: 'Pulmonary', searchTerms: ['pulmonary', 'lung', 'pulm'], icon: 'fa-lungs', color: 'primary' },
    { name: 'Cardiology', searchTerms: ['cardiology', 'cardio', 'cardiac'], icon: 'fa-heart-pulse', color: 'danger' },
    { name: 'GI', searchTerms: ['gi', 'gastroenterology', 'gastro'], icon: 'fa-pills', color: 'warning' },
    { name: 'Neurology', searchTerms: ['neurology', 'neuro', 'neurological'], icon: 'fa-brain', color: 'info' },
    // { name: 'ENT', searchTerms: ['ent', 'otolaryngology', 'ear nose throat'], icon: 'fa-ear-listen', color: 'success' },
    { name: 'Anesthesia', searchTerms: ['anesthesia', 'anesthesiology'], icon: 'fa-syringe', color: 'secondary' },
    // { name: 'Others', searchTerms: ['other', 'misc', 'miscellaneous'], icon: 'fa-ellipsis', color: 'dark' },
  ];

  const [formData, setFormData] = useState({
    referral_type_id: '',
    status_id: '',
    facility_id: null, // FK to existing facility
    facility_name: '', // For creating new facility
    contact_full_name: '', // For creating new contact
    contact_phone: '',
    contact_email: '',
    item_notes: '',
    deadline: '',
  });

  const [showOptional, setShowOptional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Facility search state
  const [facilitySearchTerm, setFacilitySearchTerm] = useState('');
  const [facilitySearchResults, setFacilitySearchResults] = useState([]);
  const [facilitySearching, setFacilitySearching] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);

  // Contact search state
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [contactSearching, setContactSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Phone number state (for PhoneNumberManager)
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [currentPhoneE164, setCurrentPhoneE164] = useState('');
  const [currentExtension, setCurrentExtension] = useState('');

  // Get default status on mount
  useEffect(() => {
    if (referralStatuses && referralStatuses.length > 0) {
      const defaultStatus = referralStatuses.find(s => s.is_default) || referralStatuses[0];
      setFormData(prev => ({ ...prev, status_id: defaultStatus.id }));
    }
  }, [referralStatuses]);

  // Debounced facility search
  const searchFacilities = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setFacilitySearchResults([]);
        return;
      }

      setFacilitySearching(true);
      try {
        const response = await adminApi.get('admin/organizations/search/', {
          params: {
            search: searchTerm,
            referral_id: referral.id,
            page_size: 10
          }
        });

        if (response.data?.success) {
          setFacilitySearchResults(response.data.organizations || []);
        }
      } catch (err) {
        console.error('Failed to search facilities:', err);
      } finally {
        setFacilitySearching(false);
      }
    }, 300),
    [referral.id]
  );

  // Debounced contact search
  const searchContacts = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setContactSearchResults([]);
        return;
      }

      setContactSearching(true);
      try {
        const response = await adminApi.get('admin/prospects/search/', {
          params: {
            search: searchTerm,
            referral_id: referral.id,
            page_size: 10
          }
        });

        if (response.data?.success) {
          setContactSearchResults(response.data.prospects || []);
        }
      } catch (err) {
        console.error('Failed to search contacts:', err);
      } finally {
        setContactSearching(false);
      }
    }, 300),
    [referral.id]
  );

  const resetForm = () => {
    setFormData({
      referral_type_id: '',
      status_id: referralStatuses?.find(s => s.is_default)?.id || '',
      facility_id: null,
      facility_name: '',
      contact_full_name: '',
      contact_email: '',
      item_notes: '',
      deadline: '',
    });
    setShowOptional(false);
    setError('');
    setSelectedFacility(null);
    setFacilitySearchTerm('');
    setFacilitySearchResults([]);
    setSelectedContact(null);
    setContactSearchTerm('');
    setContactSearchResults([]);
    setPhoneNumbers([]);
    setCurrentPhoneE164('');
    setCurrentExtension('');
  };

  const handleSave = async (addAnother = false) => {
    setError('');

    if (!formData.referral_type_id) {
      setError('Please select a referral type');
      return;
    }

    // Validate facility is provided
    if (!selectedFacility && !facilitySearchTerm.trim()) {
      setError('Please enter a facility name');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        referral_id: referral.id,
        referral_type_id: formData.referral_type_id,
        status_id: formData.status_id,
        ...(formData.item_notes && { item_notes: formData.item_notes }),
        ...(formData.deadline && { deadline: formData.deadline }),
      };

      // Handle facility - either FK to existing or create new
      if (selectedFacility) {
        payload.facility_id = selectedFacility.id;
      } else if (facilitySearchTerm.trim()) {
        // User typed but didn't select - create new
        payload.facility_data = {
          name: facilitySearchTerm.trim(),
          type: 'Clinic'
        };
      }

      // Handle contact - either link to existing or create new
      if (selectedContact) {
        // Link to existing contact
        payload.contact_id = selectedContact.id;

        // If phone changed from original, include contact_data to create new phone
        const originalPhone = selectedContact.phone_numbers?.[0]?.phone || '';
        if (currentPhoneE164 && currentPhoneE164 !== originalPhone) {
          payload.contact_data = {
            phone: currentPhoneE164,
            phone_type: 'Office',
            is_primary: true,
            role: 'Facility Contact'
          };
        }
      } else if (formData.contact_full_name || currentPhoneE164) {
        // Create new contact
        payload.contact_data = {
          full_name: formData.contact_full_name || 'Unknown',
          phone: currentPhoneE164 || '',
          email: formData.contact_email || '',
          phone_type: 'Office',
          is_primary: true,
          role: 'Facility Contact'
        };
      }

      const response = await adminApi.post('admin/referral-items/', payload);

      if (response.data?.success) {
        const newItem = response.data.referral_item;

        if (addAnother) {
          // Keep form open, reset for next item
          resetForm();
          onSuccess(newItem);
        } else {
          // Close form
          onSuccess(newItem);
          if (onCancel) onCancel();
        }
      } else {
        setError('Failed to create referral item');
      }
    } catch (err) {
      console.error('Failed to create referral item:', err);
      const errorMsg = err.response?.data?.errors || err.response?.data?.error || err.message || 'Failed to create referral item';
      setError(typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-sm mb-3">
            {typeof error === 'object' ? JSON.stringify(error) : error}
          </div>
        )}

        {/* Referral Type - 40/60 split */}
        <div className="mb-3">
          <label className="form-label fw-bold mb-2">
            Referral Type <span className="text-danger">*</span>
          </label>
          <div className="row g-2">
            {/* Left: Icon buttons (40%) */}
            <div className="col-md-4">
              <div className="d-flex flex-wrap gap-2">
                {commonReferralTypes.map((type) => {
                  // Match using search terms with word boundaries
                  const matchingType = referralTypes?.find(rt => {
                    const name = rt.name?.toLowerCase() || '';
                    return type.searchTerms.some(term => {
                      const searchTerm = term.toLowerCase();
                      // Check if term appears as a whole word or at start/end
                      const regex = new RegExp(`\\b${searchTerm}\\b|^${searchTerm}|${searchTerm}$`, 'i');
                      return regex.test(name);
                    });
                  });
                  const isSelected = formData.referral_type_id === matchingType?.id;
                  const isDisabled = !matchingType;

                  return (
                    <button
                      key={type.name}
                      type="button"
                      onClick={() => {
                        if (matchingType) {
                          setFormData(prev => ({ ...prev, referral_type_id: matchingType.id }));
                        }
                      }}
                      disabled={isDisabled}
                      className={`btn btn-sm ${isSelected ? `btn-${type.color}` : `btn-outline-${type.color}`}`}
                      title={matchingType ? matchingType.name : `${type.name} (not available)`}
                    >
                      <i className={`fas ${type.icon}`}></i>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: Dropdown (60%) */}
            <div className="col-md-8">
              <select
                value={formData.referral_type_id}
                onChange={(e) => setFormData(prev => ({ ...prev, referral_type_id: e.target.value }))}
                className="form-select form-select-sm"
              >
                <option value="">Select Referral Type</option>
                {referralTypes?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {/* {formData.referral_type_id && (
                <small className="text-success d-block mt-1">
                  ✓ {referralTypes?.find(rt => rt.id === formData.referral_type_id)?.name} selected
                </small>
              )} */}
            </div>
          </div>
        </div>

        {/* Facility Name - Auto-search */}
        <div className="mb-3">
          <label className="form-label fw-bold">Facility/Clinic Name</label>

          {selectedFacility && (
            <div className="alert alert-success alert-sm d-flex justify-content-between align-items-center mb-2 py-1">
              <small><i className="fas fa-building me-2"></i>{selectedFacility.full_name}</small>
              <button
                type="button"
                className="btn btn-sm btn-danger py-0 px-1"
                onClick={() => {
                  setSelectedFacility(null);
                  setFacilitySearchTerm('');
                  setFacilitySearchResults([]);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}

          <div className="position-relative">
            {!selectedFacility && (<input
              type="text"
              className="form-control form-control-sm"
              placeholder="Start typing to search or create new..."
              value={facilitySearchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setFacilitySearchTerm(value);
                searchFacilities(value);
              }}
              disabled={selectedFacility !== null}
            />)}
            {facilitySearching && (
              <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            {!selectedFacility && facilitySearchResults.length > 0 && (
              <div className="list-group mt-1 position-absolute w-100" style={{zIndex: 1000, boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                {facilitySearchResults.map(facility => (
                  <button
                    key={facility.id}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => {
                      setSelectedFacility(facility);
                      setFacilitySearchResults([]);
                    }}
                  >
                    <i className="fas fa-building me-2"></i>{facility.full_name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!selectedFacility && facilitySearchTerm && facilitySearchResults.length === 0 && !facilitySearching && (
            <small className="text-muted d-block mt-1">
              <i className="fas fa-info-circle me-1"></i>Will create new facility: &ldquo;{facilitySearchTerm}&rdquo;
            </small>
          )}
        </div>

        {/* Contact Fields - Auto-search */}
        <div className="mb-3">
          <label className="form-label fw-bold">Facility Contact (Optional)</label>

          {selectedContact && (
            <div className="alert alert-success alert-sm d-flex justify-content-between align-items-center mb-2 py-1">
              <small>
                <i className="fas fa-user me-2"></i>{selectedContact.full_name}
                {selectedContact.phone_numbers && selectedContact.phone_numbers.length > 0 && (
                  <span className="text-muted ms-2">• {selectedContact.phone_numbers[0].phone}</span>
                )}
              </small>
              <button
                type="button"
                className="btn btn-sm btn-danger py-0 px-1"
                onClick={() => {
                  setSelectedContact(null);
                  setContactSearchTerm('');
                  setContactSearchResults([]);
                  setPhoneNumbers([]);
                  setFormData({ ...formData, contact_full_name: '', contact_email: '' });
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}

          <div className="row g-2">
            <div className="col-12">
              <div className="position-relative">
                {!selectedContact && (<input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Start typing to search or create new..."
                  value={selectedContact ? selectedContact.full_name : contactSearchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setContactSearchTerm(value);
                    setFormData({ ...formData, contact_full_name: value });
                    searchContacts(value);
                  }}
                  disabled={selectedContact !== null}
                />)}
                {contactSearching && (
                  <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
                {!selectedContact && contactSearchResults.length > 0 && (
                  <div className="list-group mt-1 position-absolute w-100" style={{zIndex: 1000, boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                    {contactSearchResults.map(contact => (
                      <button
                        key={contact.id}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => {
                          setSelectedContact(contact);
                          setContactSearchResults([]);
                          // Auto-populate phone numbers and email from contact
                          setPhoneNumbers(contact.phone_numbers || []);
                          setFormData({
                            ...formData,
                            contact_full_name: contact.full_name,
                            contact_email: contact.email || ''
                          });
                        }}
                      >
                        <i className="fas fa-user me-2"></i>{contact.full_name}
                        {contact.phone_numbers && contact.phone_numbers.length > 0 && (
                          <span className="text-muted"> - {contact.phone_numbers[0].phone}</span>
                        )}
                        {contact.email && <span className="text-muted"> - {contact.email}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {!selectedContact && contactSearchTerm && contactSearchResults.length === 0 && !contactSearching && (
                <small className="text-muted d-block mt-1">
                  <i className="fas fa-info-circle me-1"></i>Will create new contact: &ldquo;{contactSearchTerm}&rdquo;
                </small>
              )}
            </div>
            <div className="col-12">
              <label className="form-label fw-bold mb-1">Phone Number (Optional)</label>
              <PhoneNumberManager
                phoneNumbers={phoneNumbers}
                onSave={(updatedPhones) => setPhoneNumbers(updatedPhones)}
                onChange={(phoneE164, extension) => {
                  setCurrentPhoneE164(phoneE164);
                  setCurrentExtension(extension);
                }}
                disabled={false}
                showDescription={false}
                showSaveButtons={false}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label fw-bold mb-1">Email (Optional)</label>
              <input
                type="email"
                className="form-control form-control-sm"
                placeholder="contact@example.com"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Status Selection */}
        <div className="mb-3">
          <label className="form-label fw-bold">Status (Optional)</label>
          <div className="btn-group d-flex" role="group">
            {referralStatuses?.slice(0, 4).map(status => (
              <React.Fragment key={status.id}>
                <input
                  type="radio"
                  className="btn-check"
                  name="status"
                  id={`status-${status.id}`}
                  checked={formData.status_id === status.id}
                  onChange={() => setFormData({ ...formData, status_id: status.id })}
                />
                <label className="btn btn-outline-secondary btn-sm" htmlFor={`status-${status.id}`}>
                  {status.icon && `${status.icon} `}{status.name}
                </label>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Optional Fields */}
        <div className="mb-2">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => setShowOptional(!showOptional)}
          >
            <i className={`fas fa-chevron-${showOptional ? 'up' : 'down'} me-1`}></i>
            {showOptional ? 'Hide' : 'Show'} Optional Fields
          </button>
        </div>

        {showOptional && (
          <div className="border-top pt-3 mb-3">
            <div className="mb-3">
              <label className="form-label fw-bold">Deadline</label>
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-bold">Notes</label>
              <textarea
                className="form-control form-control-sm"
                rows="3"
                placeholder="Additional notes for this referral item..."
                value={formData.item_notes}
                onChange={(e) => setFormData({ ...formData, item_notes: e.target.value })}
              ></textarea>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleSave(true)}
            disabled={loading || !formData.referral_type_id}
          >
            Save & Add More
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => handleSave(false)}
            disabled={loading || !formData.referral_type_id}
          >
            Save
          </button>

          {onCancel && (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddReferralItemForm;
